import { ChatMessage } from "interface/communications"
import { asDate } from "utils/date"

/**
 * Groups an array of chat messages by sender, maintaining the original order.
 * Messages from the same sender are grouped together into subarrays.
 *
 * @param messages - Array of chat messages to be grouped
 * @returns Array of message groups, where each group contains messages from the same sender
 * @example
 * // Given messages:
 * // [{ sender: 'user1', ... }, { sender: 'user1', ... }, { sender: 'user2', ... }]
 * // Returns:
 * // [[user1_msg1, user1_msg2], [user2_msg1]]
 */
export function groupChatMessagesBySender(messages: ChatMessage[]) {
	if (messages.length === 0) {
		return []
	} else if (messages.length === 1) {
		return [messages]
	}

	const messageGroups: ChatMessage[][] = []

	const firstMessage = messages[0]
	let chats: ChatMessage[] = []
	let referenceSender = firstMessage.sender

	messages.forEach((currentMessage, index) => {
		const currentSender = currentMessage.sender
		const isSameSender = currentSender === referenceSender
		if (isSameSender) {
			chats.push(currentMessage)
		} else {
			messageGroups.push(chats)
			chats = [currentMessage]
			referenceSender = currentSender
		}

		const isLastMessage = index === messages.length - 1
		if (isLastMessage) {
			messageGroups.push(chats)
		}
	})

	return messageGroups
}

/**
 * Groups chat messages by time intervals. Messages within the specified time duration
 * of each other are grouped together.
 *
 * @param messages - Array of chat messages to be grouped
 * @param durationMinutes - Optional time interval in minutes (default: 60) for grouping messages
 * @returns Array of message groups, where each group contains messages within the specified time interval
 * @example
 * // With default 60-minute interval:
 * // Messages at 14:00, 14:30, and 15:10 would be grouped as:
 * // [[14:00_msg, 14:30_msg], [15:10_msg]]
 */
export function groupChatMessagesByTime(
	messages: ChatMessage[],
	durationMinutes: number = 60,
): ChatMessage[][] {
	if (messages.length === 0) {
		return []
	} else if (messages.length === 1) {
		return [messages]
	}
	const messageGroups: ChatMessage[][] = []

	let referenceMessage = messages[0]
	let chats: ChatMessage[] = []

	messages.forEach((currentMessage, index) => {
		const currentMessageTime = asDate(currentMessage.timestamp)
		const isMessageWithinInterval =
			Math.abs(
				currentMessageTime.diff(asDate(referenceMessage.timestamp), "minute"),
			) <= durationMinutes
		if (isMessageWithinInterval) {
			chats.push(currentMessage)
		} else {
			messageGroups.push(chats)
			chats = [currentMessage]
			referenceMessage = currentMessage
		}

		const isLastMessage = index === messages.length - 1
		if (isLastMessage) {
			messageGroups.push(chats)
		}
	})

	return messageGroups
}

/**
 * Groups an array of chat messages by type, maintaining the original order.
 * Messages of the same type are grouped together into subarrays.
 *
 * @param messages - Array of chat messages to be grouped
 * @returns Array of message groups, where each group contains messages of the same type
 * @example
 * // Given messages:
 * // [{ type: 'message', ... }, { type: 'message', ... }, { type: 'email', ... }]
 * // Returns:
 * // [[msg_type_1, msg_type_2], [email_type_1]]
 */
export function groupChatMessagesByType(messages: ChatMessage[]) {
	if (messages.length === 0) {
		return []
	} else if (messages.length === 1) {
		return [messages]
	}

	const messageGroups: ChatMessage[][] = []

	const firstMessage = messages[0]
	let chats: ChatMessage[] = []
	let referenceType = firstMessage.type

	messages.forEach((currentMessage, index) => {
		const currentType = currentMessage.type
		const isSameType = currentType === referenceType
		if (isSameType) {
			chats.push(currentMessage)
		} else {
			messageGroups.push(chats)
			chats = [currentMessage]
			referenceType = currentType
		}

		const isLastMessage = index === messages.length - 1
		if (isLastMessage) {
			messageGroups.push(chats)
		}
	})

	return messageGroups
}

/**
 * Groups chat messages first by time intervals, then by type, and finally by sender within each type group.
 * This is the main grouping function used to organize messages for display in the chat UI.
 *
 * @param messages - Array of chat messages to be grouped, or undefined
 * @returns Array of grouped messages, organized by time, type and sender
 * @example
 * // Given messages at different times, with different types and from different senders:
 * // Returns:
 * // [
 * //   {
 * //     time: "2025-01-31T14:30:00",
 * //     messages: [
 * //       { senderInfo: user1Info, type: "message", messages: [msg1, msg2] },
 * //       { senderInfo: user1Info, type: "email", messages: [msg3] },
 * //       { senderInfo: user2Info, type: "message", messages: [msg4] }
 * //     ]
 * //   },
 * //   ...
 * // ]
 */
export function groupMessages(messages: ChatMessage[] | undefined) {
	if (!messages || messages.length === 0) {
		return []
	}

	const result = groupChatMessagesByTime(messages)
		.map((chatsByTime) => {
			// Add time metadata
			return {
				time: asDate(chatsByTime[0].timestamp),
				chatsByTime,
			}
		})
		.map(({ time, chatsByTime }) => {
			// Add type and sender metadata
			const messagesWithSenderInfo = groupChatMessagesByType(
				chatsByTime,
			).flatMap((chatsByType) => {
				return groupChatMessagesBySender(chatsByType).map((chatsBySender) => {
					return {
						sender: chatsBySender[0].sender,
						senderInfo: chatsBySender[0].senderInfo,
						type: chatsBySender[0].type,
						messages: chatsBySender,
					}
				})
			})
			return { time, messages: messagesWithSenderInfo }
		})

	return result
}
